import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import ENV from 'donor-elf-web/config/environment';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import Store from '@ember-data/store';
import StarredUserFundModel from 'donor-elf-web/models/starred-user-fund';
import FundsService, {
  AllFundsInfo,
} from 'donor-elf-web/services/funds-service';
import fuzzysort from 'fuzzysort';

class FundItem {
  constructor(
    public id: string,
    public name: string,
    starred?: boolean,
  ) {
    this.namePrepared = fuzzysort.prepare(name);
    this.starred = starred || false;
  }

  namePrepared: Fuzzysort.Prepared;
  @tracked starred: boolean;
}

export default class FundsIndexController extends Controller {
  @service declare currentUser: any;
  @service declare fundsService: FundsService;
  @service declare store: Store;

  @tracked loading = true;
  starredUserFunds: StarredUserFundModel[] = [];
  allFundsInfo?: AllFundsInfo;
  @tracked fundItems: FundItem[] = [];

  queryParams = ['q', 'page'];
  @tracked q = '';
  @tracked page = 1;
  perPage = 50;
  @tracked selectedIndex = 0;

  setAndSortTheFundItems() {
    let starredByFundIds: { [fundId: string]: boolean } = {};
    this.starredUserFunds.forEach((userFund) => {
      starredByFundIds[userFund.fundId] = userFund.starred;
    });
    let fundItems = this.allFundsInfo!.funds.map((fund) => {
      return new FundItem(fund.id, fund.name, starredByFundIds[fund.id]);
    });
    this.fundItems = sortByProperties<FundItem>(fundItems, [
      'starred:desc',
      'name:asc',
    ]);
  }

  get filteredFundItems() {
    if (isEmpty(this.q) || this.q.length < 2) {
      return this.fundItems;
    }
    return fuzzysort
      .go(this.q, this.fundItems, { keys: ['namePrepared'] })
      .map((result) => result.obj);
  }

  get pagedFundItems() {
    let offset = (this.page - 1) * this.perPage;
    return this.filteredFundItems.slice(offset, offset + this.perPage);
  }

  get totalPages() {
    return Math.ceil(this.filteredFundItems.length / this.perPage);
  }

  get rootURL() {
    //@ts-expect-error
    return ENV.rootURL;
  }

  async loadStarredUserFunds() {
    this.starredUserFunds = (
      await this.store.query('starred-user-fund', {})
    ).slice();
  }

  async loadFunds() {
    this.allFundsInfo = await this.fundsService.getAllFundsInfo();
    this.allFundsInfo.onRecordsFromServer((_args) => {
      this.setAndSortTheFundItems();
    });
  }

  @action
  async templateDidInsert() {
    this.loading = true;
    await this.loadStarredUserFunds();
    await this.loadFunds();
    this.setAndSortTheFundItems();
    this.loading = false;
  }

  @action
  templateWillDestroy() {
    this.loading = true;
    this.q = '';
    this.page = 1;
    this.selectedIndex = 0;
  }

  @action
  handleSearchKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.selectedIndex + 1 < this.filteredFundItems.length) {
        this.incrementProperty('selectedIndex');
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.selectedIndex > 0) {
        this.decrementProperty('selectedIndex');
      }
    } else if (e.key === 'Escape') {
      this.q = '';
    }
  }

  @action
  handleSearchInput(e: any) {
    this.q = e.target.value;
    this.page = 1;
    this.selectedIndex = 0;
  }

  @action
  handleSearchEnter(e: any) {
    if (e.target.value !== (this.q || '')) {
      this.handleSearchInput(e);
    }
    let selectedFund = this.filteredFundItems[this.selectedIndex];
    if (selectedFund) {
      this.fundsService.viewFund(selectedFund.id);
    }
  }

  @action
  toggleFundStarred(fundItem: FundItem) {
    let starredUserFund = this.starredUserFunds.find(
      (x) => x.fundId === fundItem.id,
    );
    if (starredUserFund) {
      starredUserFund.starred = !starredUserFund.starred;
      starredUserFund.save();
    } else {
      let newStarredUserFund = this.store.createRecord('starred-user-fund', {
        fundId: fundItem.id,
        starred: !fundItem.starred,
      });
      newStarredUserFund.save();
    }
    fundItem.starred = !fundItem.starred;
  }

  @action
  viewFund(fundId: string, event: any) {
    if (event.metaKey || event.ctrlKey) {
      // let the browser open a new tab if the cmd or ctrl key is pressed
    } else {
      event.stopPropagation();
      this.fundsService.viewFund(fundId);
    }
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
    this.selectedIndex = 0;
  }
}
